import {
    GoogleAuthProvider,
    User,
    UserCredential,
    applyActionCode,
    createUserWithEmailAndPassword,
    onAuthStateChanged,
    reload,
    signInWithCredential,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut
} from 'firebase/auth';
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { environment } from '../config/environment';
import { auth } from '../config/firebase';
import { useTransactionalEmailService } from '../services/transactionalEmailService';
import { getUserDetails } from '../services/user-details.service';
import { useUserService } from '../services/user.service';
import { UserDetails } from '../services/user.types';
import { hideCelloReferralWidget } from '../utils/domUtils';

interface AuthContextType {
    currentUser: User | null;
    currentCtUser: UserDetails | null;
    token: string | null;
    setToken: (newToken: string) => void;
    login: (email: string, password: string) => Promise<User | null>;
    signup: (email: string, password: string) => Promise<User | null>;
    logout: () => Promise<void>;
    loginWithGoogle: (redirectBackTo?: string ) => Promise<User | null>;
    resetPassword: (email: string) => Promise<void>;
    loginAfterReset: (email: string, password: string) => Promise<User | null>;
    sendVerificationEmail: () => Promise<void>;
    verifyEmail: (oobCode: string) => Promise<void>;
    hasTokenClaim: (claimName: string) => boolean;
    refreshToken: () => Promise<string | null>;
    reloadUser: () => Promise<void>;

    isProcessingAuth: boolean;
    setIsProcessingAuth: (value: boolean) => void;
    isNewUser: boolean;
    setIsNewUser: (value: boolean) => void;
    isInitialized: boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [currentCtUser, setCurrentCtUser] = useState<UserDetails | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [tokenClaims, setTokenClaims] = useState<Record<string, any>>({});
    const [isProcessingAuth, setIsProcessingAuth] = useState<boolean>(false);
    const [isNewUser, setIsNewUser] = useState<boolean>(false);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const emailService = useTransactionalEmailService();
    const userService = useUserService();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const signup = async (email: string, password: string) => {
        try {
            setIsProcessingAuth(true);
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);

            // Use the new service for email verification
            await emailService.sendEmailConfirmation(email);

            // Set current user but don't redirect yet - the SignupPage component
            // will handle redirection to the verification page
            setCurrentUser(userCredential.user);

            // Check if this is actually a new user (creation time equals last sign-in time)
            // This helps prevent showing onboarding to existing users who try to sign up again
            if (userCredential.user.metadata.creationTime === userCredential.user.metadata.lastSignInTime) {
                setIsNewUser(true);
            } else {
                setIsNewUser(false);
            }

            return userCredential.user;
        } finally {
            setIsProcessingAuth(false);
        }
    };

    const login = async (email: string, password: string) => {
        try {
            setIsProcessingAuth(true);
            const userCredential = await signInWithEmailAndPassword(auth, email, password);

            // If this is a first-time login (no lastLoginAt timestamp or it's the same as createdAt)
            if (userCredential.user.metadata.creationTime === userCredential.user.metadata.lastSignInTime) {
                setIsNewUser(true);
            }

            if (
                !userCredential.user.emailVerified &&
                !userCredential.user.providerData.some((provider) => provider.providerId === 'google.com')
            ) {
                // Send a new verification email if not verified using the new service
                await emailService.sendEmailConfirmation(email);
                // Keep the user logged in so they can access the verification page
            }

            return userCredential.user;
        } finally {
            setIsProcessingAuth(false);
        }
    };

    const loginWithGoogle = async (redirectBackTo = null) => {
        try {
            console.log('[Google Auth] Starting Google login...');
            setIsProcessingAuth(true);

            const isProduction = environment.production;
            if (isProduction) {
                await loginWithGoogleRedirect(redirectBackTo);
            } else {
                await loginWithGooglePopup();
            }
            return null;
        } catch (error) {
            console.error('[Google Auth] Error during Google authentication:', error);
            throw error; 
        } finally {
            setIsProcessingAuth(false);
        }
    };

    const loginWithGooglePopup = async () => {
        // Popup flow - handle result immediately
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
            prompt: 'select_account',
            login_hint: 'info@coinfox.io',
        });
        const result = await signInWithPopup(auth, provider);
        await finalizeLoginWithGoogle(result);
    }

    const loginWithGoogleRedirect = (redirectBackTo = null) => {
        const params = new URLSearchParams({
          client_id: environment.googeClientId,
          redirect_uri: environment.googleLoginCallbackUrl,
          response_type: 'code',
          scope: 'openid email profile',
          prompt: 'select_account',
        });
        if (redirectBackTo) 
            params.append('state', JSON.stringify({ redirectTo: redirectBackTo }));
      
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    }

    useEffect(() => {
        const handleAuth = async () => {
          const url = new URL(window.location.href);
          const code = url.searchParams.get("code");
          const state = JSON.parse(url.searchParams.get("state") || "{}"); // Not used for now but can be utilized to redirect on the page where the user was
    
          if (!code) return; // Exit if no code parameter is found

          try {
            const id_token: string = await userService.getIdToken(code);     
      
            // Sign in to Firebase using the returned ID token
            const credential = GoogleAuthProvider.credential(id_token);
            const userCredential = await signInWithCredential(auth, credential);
            finalizeLoginWithGoogle(userCredential, state.redirectTo);
          } catch (error) {
            console.error('[Google Auth] Error handling Google authentication callback:', error);
          }
        };
    
        handleAuth();
    }, []);

    const finalizeLoginWithGoogle = async (loginResult: UserCredential | null, redirectTo: string|null = null) => {
        if (loginResult && loginResult.user) {
            setCurrentUser(loginResult.user);
            hideCelloReferralWidget();

            // Check if this is a new user
            if (loginResult.user.metadata.creationTime === loginResult.user.metadata.lastSignInTime) {
                if (loginResult.user.email) {
                    await emailService.sendWelcomeEmail(loginResult.user.email);
                }
                setIsNewUser(true);
            }

            // Fetch user data
            fetchUserData(loginResult.user);
            navigate(redirectTo || '/dashboard');
        }
    };

    const logout = async () => {
        removeUser();
        await signOut(auth);
    };

    const removeUser = () => {
        setCurrentCtUser(null);
        setCurrentUser(null);
        setToken(null);
        setTokenClaims({});
        setIsNewUser(false);
        setIsInitialized(true);
        queryClient.clear();
    };

    const setAuthToken = (newToken: string) => {
        setToken(newToken);
        // Parse JWT token to extract claims
        try {
            const base64Url = newToken.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            const claims = JSON.parse(jsonPayload);
            setTokenClaims(claims);
        } catch (error) {
            console.error('Error parsing token claims:', error);
            setTokenClaims({});
        }
    };

    const resetPassword = async (email: string) => {
        // Use the new service for password reset instead of Firebase's direct method
        await emailService.sendPasswordReset(email);
    };

    const loginAfterReset = async (email: string, password: string) => {
        try {
            setIsProcessingAuth(true);
            // Use the same signInWithEmailAndPassword but don't send verification email
            const userCredential = await signInWithEmailAndPassword(auth, email, password);

            // Check if this is a new user (unlikely for password reset)
            if (userCredential.user.metadata.creationTime === userCredential.user.metadata.lastSignInTime) {
                setIsNewUser(true);
            } else {
                setIsNewUser(false);
            }

            return userCredential.user;
        } finally {
            setIsProcessingAuth(false);
        }
    };

    const sendVerificationEmail = async () => {
        if (currentUser && !currentUser.emailVerified) {
            await emailService.sendEmailConfirmation(currentUser.email || '');
        }
    };

    const verifyEmail = async (oobCode: string) => {
        return applyActionCode(auth, oobCode);
    };

    // Check if a claim exists in the token
    const hasTokenClaim = (claimName: string) => {
        return tokenClaims.hasOwnProperty(claimName);
    };

    // Fetch user details and settings
    const fetchUserData = async (user: User) => {
        try {
            const token = await user.getIdToken();
            setAuthToken(token); // Use setAuthToken instead of setToken to also parse claims

            const details = await getUserDetails(token);
            setCurrentCtUser(details);

            return details;
        } catch (error) {
            console.error('Error fetching user details:', error);
            return null;
        }
    };

    // Add the refreshToken function
    const refreshToken = async (): Promise<string | null> => {
        if (!currentUser) {
            return null;
        }

        try {
            const newToken = await currentUser.getIdToken(true); // Force refresh
            setAuthToken(newToken);
            return newToken;
        } catch (error) {
            console.error('Error refreshing token:', error);
            return null;
        }
    };

    const reloadUser = async () => {
        if (!currentUser) {
            return;
        }
        try {
            await reload(currentUser);
            const token = await currentUser.getIdToken();
            setAuthToken(token); // Update the token
            const details = await getUserDetails(token);
            setCurrentCtUser(details);
        } catch (error) {
            console.error('Error reloading user:', error);
        }
    };

    // Handle Firebase authentication state changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            console.log('[Auth] Auth state changed:', user ? `User logged in (${user.email})` : 'User logged out');

            if (user) {
                setCurrentUser(user);

                // For normal auth state changes, fetch user data
                await fetchUserData(user);
            } else {
                removeUser();
            }

            setIsInitialized(true);
            setIsProcessingAuth(false);
        });

        return () => unsubscribe();
    }, []);

    const value = {
        currentUser,
        currentCtUser,
        token,
        setToken: setAuthToken,
        login,
        signup,
        logout,
        isInitialized,
        loginWithGoogle,
        resetPassword,
        loginAfterReset,
        sendVerificationEmail,
        verifyEmail,
        hasTokenClaim,
        refreshToken,
        reloadUser,

        isProcessingAuth,
        setIsProcessingAuth,
        isNewUser,
        setIsNewUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
